'use client';

/**
 * RemoveUserDialog Component
 *
 * Confirmation dialog for removing a user from an organization.
 * This is a soft removal - sets organization_id to null rather than deleting the user.
 *
 * @example
 * ```tsx
 * <RemoveUserDialog
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   user={{
 *     id: 'user-123',
 *     email: 'user@example.com',
 *     full_name: 'John Doe'
 *   }}
 *   onSuccess={() => console.log('Removed')}
 * />
 * ```
 */

import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Inline,
  List,
  ListItem,
  Stack,
  Text,
} from '@cortex/ui';
import { AlertTriangle } from 'lucide-react';
import * as React from 'react';

import { useFetchWithAuth } from '@/hooks/use-api';

interface RemoveUserDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;

  /**
   * Callback when dialog should close
   */
  onClose: () => void;

  /**
   * User to remove
   */
  user: {
    id: string;
    email: string;
    full_name: string | null;
  };

  /**
   * Callback when user is successfully removed
   */
  onSuccess?: () => void;
}

export function RemoveUserDialog({
  open,
  onClose,
  user,
  onSuccess,
}: RemoveUserDialogProps) {
  const fetchWithAuth = useFetchWithAuth();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!open) {
      setLoading(false);
      setError(null);
    }
  }, [open]);

  const handleRemove = async () => {
    setError(null);
    setLoading(true);

    try {
      const response = await fetchWithAuth(`/admin/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_id: null }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove user');
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const displayName = user.full_name || user.email.split('@')[0];

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Remove User</DialogTitle>
          <DialogDescription>Remove user from organization</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Stack gap="md">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Alert variant="warning">
              <AlertTriangle className="h-5 w-5" />
              <AlertDescription>
                <Text weight="medium" size="sm">
                  Are you sure you want to remove this user?
                </Text>
                <Text size="sm" color="muted" className="mt-1">
                  This action will remove{' '}
                  <Text as="span" weight="medium">
                    {displayName}
                  </Text>{' '}
                  ({user.email}) from this organization.
                </Text>
              </AlertDescription>
            </Alert>

            <Card className="bg-surface-2">
              <CardContent className="space-y-2 p-3">
                <Text size="sm" weight="medium">
                  What happens next:
                </Text>
                <List gap="xs">
                  <ListItem>
                    <Inline gap="sm" align="start">
                      <Text as="span" color="secondary">
                        •
                      </Text>
                      <Text as="span" color="muted" size="sm">
                        The user will lose access to all organization resources
                      </Text>
                    </Inline>
                  </ListItem>
                  <ListItem>
                    <Inline gap="sm" align="start">
                      <Text as="span" color="secondary">
                        •
                      </Text>
                      <Text as="span" color="muted" size="sm">
                        Their account will remain active (not deleted)
                      </Text>
                    </Inline>
                  </ListItem>
                  <ListItem>
                    <Inline gap="sm" align="start">
                      <Text as="span" color="secondary">
                        •
                      </Text>
                      <Text as="span" color="muted" size="sm">
                        They can be re-invited later if needed
                      </Text>
                    </Inline>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleRemove}
            loading={loading}
          >
            Remove User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
